import {
  MAX_LIVES,
  PLAY_EVENTS,
  SLOT_COUNT,
  boardFull,
  createAnalytics,
  dayCards,
  formatUtcDayLabel,
  formatYear,
  placeIntoSlot,
  returnToPool,
  shareText,
  startRound,
  submitGuess,
  utcDayNumber,
  yearFlipDurationMs,
  type HistoryEvent,
  type RoundState,
  type Schedule,
  type ScheduledDay,
} from '../../src/index.js';
import { escapeAttr, escapeHtml } from './dom.js';
import { syncSnakePath } from './snake-path.js';

declare const __POSTHOG_KEY__: string;
declare const __POSTHOG_HOST__: string;

const BOOKEND_PAST = 'Dinosaurs roamed the Earth';
const BOOKEND_NOW = 'You reading this';

const HEART_PATH =
  'M12 21.35 L10.55 20.03 C5.4 15.36 2 12.28 2 8.5 C2 5.42 4.42 3 7.5 3 C9.24 3 10.91 3.81 12 5.09 C13.09 3.81 14.76 3 16.5 3 C19.58 3 22 5.42 22 8.5 C22 12.28 18.6 15.36 13.45 20.04 L12 21.35 Z';

const root = document.getElementById('app');
if (!root) throw new Error('#app missing');

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const analytics = createAnalytics({
  key: typeof __POSTHOG_KEY__ === 'string' ? __POSTHOG_KEY__ : '',
});

let schedule: Schedule | null = null;
let state: RoundState | null = null;
let selectedId: string | null = null;
let statusMessage = '';
let revealYears = false;
let pathObserver: ResizeObserver | null = null;

function loadSchedule(): Promise<Schedule> {
  return fetch('./schedule.json', { cache: 'no-store' }).then(async (res) => {
    if (!res.ok) throw new Error(`schedule.json HTTP ${res.status}`);
    return (await res.json()) as Schedule;
  });
}

function resolveNow(): Date {
  const params = new URLSearchParams(location.search);
  const raw = params.get('now');
  if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return new Date(`${raw}T12:00:00Z`);
  }
  return new Date();
}

function todayDay(sched: Schedule, now = resolveNow()): ScheduledDay | undefined {
  return dayCards(sched, utcDayNumber(now, sched.launchDate));
}

function ensurePosthog(): void {
  const key = typeof __POSTHOG_KEY__ === 'string' ? __POSTHOG_KEY__ : '';
  if (!key) return;
  const g = globalThis as {
    posthog?: { init: (k: string, o: object) => void; capture: (e: string, p?: object) => void };
  };
  if (g.posthog) return;
  const stub = {
    init() {},
    capture(e: string, p?: object) {
      if (typeof console !== 'undefined') console.info('[posthog]', e, p);
    },
  };
  g.posthog = stub;
  stub.init(key, {
    api_host: typeof __POSTHOG_HOST__ === 'string' ? __POSTHOG_HOST__ : undefined,
  });
}

function renderEmpty(message: string): void {
  root!.innerHTML = `
    <main class="shell">
      <h1>Now and Then</h1>
      <p class="lede">${message}</p>
    </main>`;
}

function heartRow(lives: number): string {
  return Array.from({ length: MAX_LIVES }, (_, i) => {
    const on = i < lives;
    return `<svg class="heart ${on ? 'on' : 'off'}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="${HEART_PATH}"></path>
    </svg>`;
  }).join('');
}

/** Slot 1 (index 0) on the right, then alternate — matches Figma. */
function slotSide(index: number): 'left' | 'right' {
  return index % 2 === 0 ? 'right' : 'left';
}

function slotHtml(index: number, card: HistoryEvent | null): string {
  const side = slotSide(index);
  if (card) {
    return `<div class="stop slot-stop ${side}">
      <span class="hub filled" aria-hidden="true"></span>
      <button type="button" class="tile in-slot${revealYears ? ' revealed' : ''}" data-from-slot="${index}" draggable="false">
        <span class="tile-title">${escapeHtml(card.title)}</span>
        ${revealYears ? `<span class="year">${escapeHtml(formatYear(card.year))}</span>` : ''}
      </button>
    </div>`;
  }
  return `<div class="stop slot-stop ${side}">
    <button type="button" class="hub ring" data-slot="${index}" aria-label="Empty slot ${index + 1}"></button>
  </div>`;
}

function poolTile(card: HistoryEvent): string {
  const selected = selectedId === card.id ? ' selected' : '';
  return `<button type="button" class="tile pool-tile${selected}" data-card="${escapeAttr(card.id)}" aria-label="${escapeAttr(card.title)}">
    <span class="tile-title">${escapeHtml(card.title)}</span>
  </button>`;
}

function updateSnakePath(): void {
  const snake = root!.querySelector<HTMLElement>('.snake');
  const pathEl = root!.querySelector<SVGPathElement>('.snake-path path');
  if (!snake || !pathEl) return;
  syncSnakePath(snake, pathEl);
}

function observeSnakePath(): void {
  pathObserver?.disconnect();
  const snake = root!.querySelector<HTMLElement>('.snake');
  if (!snake) return;
  pathObserver = new ResizeObserver(() => updateSnakePath());
  pathObserver.observe(snake);
  requestAnimationFrame(() => updateSnakePath());
}

function render(): void {
  if (!state || !schedule) return;
  const s = state;
  const canSubmit = !s.ended && boardFull(s);
  const flipMs = yearFlipDurationMs(reducedMotion);
  const dateLabel = formatUtcDayLabel(s.day, schedule.launchDate);

  const slots = s.slots.map((card, i) => slotHtml(i, card)).join('');
  const pool = s.pool.map(poolTile).join('');

  const endBlock = s.ended
    ? `<section class="end" aria-live="polite">
        <p class="msg">${s.outcome === 'win' ? 'Perfect order — you win.' : 'Out of lives — round over.'}</p>
        <button type="button" class="share" id="share">Copy result</button>
        <p class="share-hint" id="share-hint"></p>
      </section>`
    : `<button type="button" class="submit" id="submit" ${canSubmit ? '' : 'disabled'}>Submit order</button>`;

  root!.innerHTML = `
    <main class="shell">
      <header class="top">
        <h1>Now and Then</h1>
        <p class="lede">Put today's moments in date order, then submit.</p>
        <div class="meta">
          <p class="day">Day ${s.day} - ${escapeHtml(dateLabel)}</p>
          <p class="lives" aria-live="polite" aria-label="${s.lives} of ${MAX_LIVES} lives">
            <span class="hearts">${heartRow(s.lives)}</span>
            <span class="lives-text">${s.lives} of ${MAX_LIVES} lives</span>
          </p>
        </div>
      </header>
      <section class="play" aria-label="Timeline trail">
        <div class="snake" style="--flip:${flipMs}ms">
          <svg class="snake-path" aria-hidden="true" focusable="false">
            <path d=""></path>
          </svg>
          <div class="stop bookend start left">
            <span class="hub past" aria-hidden="true"></span>
            <span class="lbl">${escapeHtml(BOOKEND_PAST)}</span>
          </div>
          ${slots}
          <div class="stop bookend end right">
            <span class="hub now" aria-hidden="true"></span>
            <span class="lbl">${escapeHtml(BOOKEND_NOW)}</span>
          </div>
        </div>
        <p class="pool-heading">Your events</p>
        <div class="pool" aria-label="Events to place">
          ${pool || '<p class="pool-empty">All events are on the trail</p>'}
        </div>
        ${endBlock}
      </section>
      <p class="status" aria-live="polite">${escapeHtml(statusMessage)}</p>
    </main>`;

  bindUi();
  observeSnakePath();
}

function bindUi(): void {
  root!.querySelectorAll<HTMLButtonElement>('.pool-tile').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.card;
      if (!id || !state || state.ended) return;
      selectedId = selectedId === id ? null : id;
      statusMessage = selectedId ? 'Selected. Tap an empty ring to place it.' : '';
      render();
    });
  });

  root!.querySelectorAll<HTMLButtonElement>('.hub.ring').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!state || state.ended || selectedId === null) return;
      const slot = Number(btn.dataset.slot);
      if (!Number.isInteger(slot)) return;
      try {
        state = placeIntoSlot(state, selectedId, slot);
        selectedId = null;
        statusMessage = boardFull(state) ? 'Board full — submit your guess.' : '';
        render();
      } catch {
        statusMessage = 'Could not place there.';
        render();
      }
    });
  });

  root!.querySelectorAll<HTMLButtonElement>('.tile.in-slot').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!state || state.ended || revealYears) return;
      const from = Number(btn.dataset.fromSlot);
      if (!Number.isInteger(from)) return;
      if (selectedId) {
        const occupying = state.slots[from];
        try {
          state = placeIntoSlot(state, selectedId, from);
          selectedId = occupying?.id ?? null;
        } catch {
          /* ignore */
        }
        statusMessage = '';
        render();
        return;
      }
      state = returnToPool(state, from);
      statusMessage = 'Returned to the pool.';
      render();
    });
  });

  document.getElementById('submit')?.addEventListener('click', () => void onSubmit());
  document.getElementById('share')?.addEventListener('click', () => void onShare());

  attachPoolDrag();
}

function attachPoolDrag(): void {
  let dragId: string | null = null;
  root!.querySelectorAll<HTMLElement>('.pool-tile').forEach((el) => {
    el.addEventListener('pointerdown', (e) => {
      if (!state || state.ended || e.button > 0) return;
      dragId = el.dataset.card ?? null;
      el.setPointerCapture(e.pointerId);
    });
    el.addEventListener('pointerup', (e) => {
      if (!dragId || !state) return;
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const hub = target?.closest?.('.hub.ring') as HTMLElement | null;
      const slot = hub ? Number(hub.dataset.slot) : NaN;
      if (Number.isInteger(slot)) {
        try {
          state = placeIntoSlot(state, dragId, slot);
          selectedId = null;
          statusMessage = boardFull(state) ? 'Board full — submit your guess.' : '';
          render();
        } catch {
          /* ignore */
        }
      }
      dragId = null;
    });
  });
}

async function onSubmit(): Promise<void> {
  if (!state || state.ended || !boardFull(state)) return;
  const result = submitGuess(state);
  analytics.capture(PLAY_EVENTS.guessSubmit, {
    day: state.day,
    correct_count: result.correctCount,
    lives_left: result.state.lives,
    won: result.won,
  });
  state = result.state;
  if (result.won) {
    revealYears = true;
    statusMessage = `${SLOT_COUNT} of ${SLOT_COUNT} correct.`;
    analytics.capture(PLAY_EVENTS.roundEnd, {
      day: state.day,
      outcome: 'win',
      attempts: state.attempts,
    });
  } else {
    revealYears = false;
    selectedId = null;
    statusMessage = `${result.correctCount} of ${SLOT_COUNT} correct. Try again.`;
    if (state.ended) {
      analytics.capture(PLAY_EVENTS.roundEnd, {
        day: state.day,
        outcome: 'loss',
        attempts: state.attempts,
      });
    }
  }
  render();
}

async function onShare(): Promise<void> {
  if (!state || !state.ended) return;
  const text = shareText(state);
  try {
    await navigator.clipboard.writeText(text);
    const hint = document.getElementById('share-hint');
    if (hint) hint.textContent = 'Copied.';
  } catch {
    const hint = document.getElementById('share-hint');
    if (hint) hint.textContent = text;
  }
  analytics.capture(PLAY_EVENTS.resultShare, {
    day: state.day,
    outcome: state.outcome,
    attempts: state.attempts,
  });
}

async function boot(): Promise<void> {
  ensurePosthog();
  analytics.capture(PLAY_EVENTS.playOpen, {});
  try {
    schedule = await loadSchedule();
  } catch {
    renderEmpty('Could not load the daily schedule.');
    return;
  }
  const day = todayDay(schedule);
  if (!day) {
    const n = utcDayNumber(new Date(), schedule.launchDate);
    renderEmpty(n < 1 ? 'Play unlocks on launch day (UTC).' : `No scheduled cards for day #${n}.`);
    return;
  }
  state = startRound(day);
  analytics.capture(PLAY_EVENTS.roundStart, {
    day: state.day,
    card_count: day.cards.length,
  });
  statusMessage = 'Tap a white tile, then an empty ring — or drag onto a ring.';
  render();
}

void boot();
